import React, { useEffect, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Outlet, useParams } from "react-router";
import {
  EmptyState,
  IconButton,
  NavigationSidebar,
  Page,
  PageNotFound,
  PageShell,
  SearchBar,
  useSidebarCollapsed,
} from "@sscs/ui";
import { SearchIcon } from "@sscs/ui/icons";
import { AllowedTo, QuotaBar } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useDialog, useGroup, useUsageQuota } from "Hooks";
import { useGetProjectsSearchQuery } from "Services";
import CreateNewButton from "../../components/CreateNewButton/CreateNewButton";
import CreateProject from "../../components/CreateProject/CreateProject";
import { useRestoreProjectsURL } from "../../hooks";
import ProjectsNavigation from "./NavigationSidebar/NavigationSidebar";
import styles from "./projectsTab.module.scss";

const ProjectsTab = (): React.JSX.Element => {
  const { groupId } = useParams();
  const { total, used } = useUsageQuota("VERSION");
  const dialog = useDialog();
  const [filterValue, setFilterValue] = useState("");
  const searchBarRef = useRef<HTMLInputElement | null>(null);
  const [isCollapsed, setIsCollapsed] = useSidebarCollapsed("projects");
  const [shouldFocusSearchBar, setShouldFocusSearchBar] = useState(false);

  const { data, isLoading, isError } = useGetProjectsSearchQuery(
    groupId
      ? {
          groupId: groupId,
          searchName: filterValue,
        }
      : skipToken,
  );

  useRestoreProjectsURL();

  useEffect(() => {
    if (!isCollapsed && shouldFocusSearchBar) {
      searchBarRef.current?.focus();
      setShouldFocusSearchBar(false);
    }
  }, [isCollapsed, shouldFocusSearchBar]);

  const renderContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <Page.Root>
          <Page.Body status={{ status: "LOADING" }} />
        </Page.Root>
      );
    }

    if (isError) {
      return (
        <PageNotFound
          title="There was a problem"
          byline="We were unable to fetch your list of projects. Try refreshing the page"
        />
      );
    }

    return (
      <>
        <Outlet />
        <NoProjectsSelected onCreateClick={dialog.open} />
      </>
    );
  };

  return (
    <>
      <PageShell
        sidebar={
          <NavigationSidebar
            aria-label="projects-navigation"
            storageKey="projects"
            title="Projects"
            className={styles.sidebar}
            isCollapsed={isCollapsed}
            onCollapse={setIsCollapsed}
          >
            <AllowedTo permissions={[PERMISSION.CREATE_PROJECT]}>
              <CreateNewButton
                id="tab-create-project"
                color="primary"
                type="project"
                onClick={dialog.open}
                showLabel={!isCollapsed}
              />
            </AllowedTo>
            {isCollapsed ? (
              <IconButton
                onClick={() => {
                  setIsCollapsed(false);
                  setShouldFocusSearchBar(true);
                }}
                tooltipTitle="Search"
                aria-label="Search"
              >
                <SearchIcon />
              </IconButton>
            ) : (
              <SearchBar
                value={filterValue}
                onChange={setFilterValue}
                debounceTimer={250}
                label="Search"
                id="projects-search"
                variant="outlined"
                slotProps={{ htmlInput: { maxLength: 256 } }}
                ref={searchBarRef}
              />
            )}
            <ProjectsNavigation
              projects={data?.projects}
              filterValue={filterValue}
              onResetSearch={() => setFilterValue("")}
            />
            <div className={styles.quota}>
              <QuotaBar
                variant="stacked"
                value={used}
                max={total}
                units="GB"
                collapsed={isCollapsed}
              />
            </div>
          </NavigationSidebar>
        }
        contentClassName={styles.content}
      >
        {renderContent()}
      </PageShell>
      <CreateProject
        isOpen={dialog.isOpen}
        groupId={groupId}
        onClose={dialog.close}
      />
    </>
  );
};

type NoProjectsSelectedProps = {
  onCreateClick: () => void;
};

const NoProjectsSelected = ({
  onCreateClick,
}: NoProjectsSelectedProps): React.JSX.Element => {
  const currentGroup = useGroup();
  const { groupId, projectId } = useParams();
  const canCreateProject = useAllowedTo({
    permissions: [PERMISSION.CREATE_PROJECT],
    groupId,
  });

  return !projectId ? (
    <Page.Root>
      <Page.Header
        title={<>Projects for group: {currentGroup?.name}</>}
        actions={
          canCreateProject ? (
            <CreateNewButton
              id="create-project"
              type="project"
              onClick={onCreateClick}
            />
          ) : undefined
        }
      />
      <Page.Body>
        <EmptyState subject="project selected" />
      </Page.Body>
    </Page.Root>
  ) : (
    <></>
  );
};
export default ProjectsTab;
